import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';

import { supabase } from './supabase';

export type Role = 'admin' | 'teacher' | 'client' | null;

export type SessionState = {
  session: Session | null;
  role: Role;
  loading: boolean;
};

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let token = 0;

    async function fetchRole(s: Session | null): Promise<Role> {
      if (!s) return null;
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', s.user.id)
        .maybeSingle<{ role: 'admin' | 'teacher' | 'client' }>();
      return (data?.role as Role) ?? 'client';
    }

    async function sync(s: Session | null) {
      const myToken = ++token;
      setLoading(true);
      const nextRole = await fetchRole(s);
      if (!mounted || myToken !== token) return;
      setSession(s);
      setRole(nextRole);
      setLoading(false);
    }

    supabase.auth.getSession().then(({ data }) => {
      if (mounted) sync(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      if (event === 'TOKEN_REFRESHED') return;
      sync(next);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, role, loading };
}

export async function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  return supabase.auth.signOut();
}
